import { useState, type FocusEvent, type MouseEvent } from 'react';
import type { ClusterResult } from '../../types/result';

interface ClusterBlueteProps {
  clusters: readonly ClusterResult[];
  mode: 'absolute' | 'relative';
}

interface TooltipState {
  x: number;
  y: number;
  name: string;
  summary: string;
  value: number;
}

const CX = 200;
const CY = 200;
const R0 = 36;
const R_MAX = 168;
const GAP_DEG = 5;
const LABEL_OFFSET = 16;

function labelAnchorFor(midDeg: number): 'start' | 'end' | 'middle' {
  const cos = Math.cos((midDeg * Math.PI) / 180);
  if (cos > 0.35) return 'start';
  if (cos < -0.35) return 'end';
  return 'middle';
}

function wrapTitle(title: string): string[] {
  const words = title.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    if ((line + ' ' + word).trim().length > 16) {
      lines.push(line.trim());
      line = word;
    } else {
      line = (line + ' ' + word).trim();
    }
  }
  if (line) lines.push(line);
  return lines;
}

/**
 * Radiales Balkendiagramm ("Blüte") der Bewertungsbereiche -- bewusst kein Radar-/Spider-Chart:
 * jedes Segment ist eigenständig, keine Verbindungslinie zwischen Nachbarwerten (das würde einen
 * Zusammenhang suggerieren, den es zwischen unabhängigen Bereichen nicht gibt).
 *
 * Die Blütenblatt-LÄNGE folgt dem gewählten `mode` (absolut oder relativ zum stärksten Bereich),
 * die angezeigten ZAHLEN (Tooltip, Spitzen-Badge, Tabellen-Fallback) zeigen immer den echten
 * Prozentwert -- damit bleibt die Blüte auch im "relative"-Modus ehrlich lesbar.
 */
export function ClusterBluete({ clusters, mode }: ClusterBlueteProps) {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const maxPercentage = Math.max(...clusters.map((cluster) => cluster.percentage), 0);
  const normalizedValues = clusters.map((cluster) => {
    if (mode === 'absolute' || maxPercentage <= 0) return cluster.percentage;
    return (cluster.percentage / maxPercentage) * 100;
  });

  const segmentCount = clusters.length;
  const stepDeg = 360 / segmentCount;
  const peakIndex = clusters.reduce(
    (best, cluster, index, arr) =>
      cluster.percentage > (arr[best]?.percentage ?? -Infinity) ? index : best,
    0,
  );

  function showTooltip(
    event: MouseEvent<SVGCircleElement> | FocusEvent<SVGCircleElement>,
    name: string,
    summary: string,
    value: number,
  ): void {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({ x: rect.left + rect.width / 2, y: rect.top, name, summary, value });
  }

  function hideTooltip(): void {
    setTooltip(null);
  }

  return (
    <div className="cluster-bluete">
      <div className="cluster-bluete__frame">
        <svg
          viewBox="-100 -40 600 480"
          role="img"
          aria-label="Deine Bewertungsbereiche als Blütendiagramm"
        >
          {[0.33, 0.66, 1].map((fraction) => (
            <circle
              key={fraction}
              cx={CX}
              cy={CY}
              r={R0 + (R_MAX - R0) * fraction}
              className={
                fraction === 1
                  ? 'cluster-bluete__grid-ring cluster-bluete__grid-ring--target'
                  : 'cluster-bluete__grid-ring'
              }
            />
          ))}
          <circle cx={CX} cy={CY} r={R0} className="cluster-bluete__core" />

          {clusters.map((cluster, i) => {
            const value = normalizedValues[i] ?? 0;
            const startDeg = -90 + i * stepDeg + GAP_DEG / 2;
            const endDeg = -90 + (i + 1) * stepDeg - GAP_DEG / 2;
            const startRad = (startDeg * Math.PI) / 180;
            const endRad = (endDeg * Math.PI) / 180;
            const r = R0 + (R_MAX - R0) * (value / 100);

            const p1 = { x: CX + R0 * Math.cos(startRad), y: CY + R0 * Math.sin(startRad) };
            const p2 = { x: CX + r * Math.cos(startRad), y: CY + r * Math.sin(startRad) };
            const p3 = { x: CX + r * Math.cos(endRad), y: CY + r * Math.sin(endRad) };
            const p4 = { x: CX + R0 * Math.cos(endRad), y: CY + R0 * Math.sin(endRad) };
            const largeArc = endDeg - startDeg > 180 ? 1 : 0;

            const d = [
              `M ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`,
              `L ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`,
              `A ${r.toFixed(2)} ${r.toFixed(2)} 0 ${largeArc} 1 ${p3.x.toFixed(2)} ${p3.y.toFixed(2)}`,
              `L ${p4.x.toFixed(2)} ${p4.y.toFixed(2)}`,
              `A ${R0} ${R0} 0 0 0 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`,
              'Z',
            ].join(' ');

            const alpha = 0.22 + (value / 100) * 0.68;
            const midRad = (startRad + endRad) / 2;
            const midDeg = (startDeg + endDeg) / 2;
            // Das Spitzenreiter-Segment hat zusätzlich eine Badge direkt an seiner Spitze
            // (bis zu R_MAX + 16 + Badge-Radius 15); sein Label braucht deshalb mehr Abstand,
            // damit sich Zahl-Badge und Achsen-Label bei einem 100%-Wert nicht überlappen.
            const labelOffset = i === peakIndex ? LABEL_OFFSET + 26 : LABEL_OFFSET;
            const labelPos = {
              x: CX + (R_MAX + labelOffset) * Math.cos(midRad),
              y: CY + (R_MAX + labelOffset) * Math.sin(midRad),
            };
            const targetCenter = {
              x: CX + ((R0 + r) / 2) * Math.cos(midRad),
              y: CY + ((R0 + r) / 2) * Math.sin(midRad),
            };
            const lines = wrapTitle(cluster.title);

            return (
              <g key={cluster.clusterId}>
                <path
                  d={d}
                  fill={`color-mix(in srgb, var(--color-accent-strong) ${Math.round(alpha * 100)}%, transparent)`}
                  stroke="var(--color-accent-strong)"
                  strokeWidth={i === peakIndex ? 1.6 : 0}
                  strokeLinejoin="round"
                />
                <circle
                  cx={targetCenter.x}
                  cy={targetCenter.y}
                  r={16}
                  className="cluster-bluete__target"
                  tabIndex={0}
                  role="img"
                  aria-label={`${cluster.title}: ${cluster.percentage} Prozent`}
                  onMouseEnter={(event) =>
                    showTooltip(event, cluster.title, cluster.summary, cluster.percentage)
                  }
                  onMouseLeave={hideTooltip}
                  onFocus={(event) =>
                    showTooltip(event, cluster.title, cluster.summary, cluster.percentage)
                  }
                  onBlur={hideTooltip}
                />
                <text
                  x={labelPos.x}
                  y={labelPos.y}
                  className="cluster-bluete__axis-label"
                  textAnchor={labelAnchorFor(midDeg)}
                >
                  {lines.map((line, li) => (
                    <tspan
                      key={line}
                      x={labelPos.x}
                      dy={li === 0 ? (lines.length > 1 ? -((lines.length - 1) * 6) : 0) : 12}
                    >
                      {line}
                    </tspan>
                  ))}
                </text>
                {i === peakIndex && (
                  <g>
                    <circle
                      cx={CX + (r + 16) * Math.cos(midRad)}
                      cy={CY + (r + 16) * Math.sin(midRad)}
                      r={15}
                      className="cluster-bluete__peak-badge"
                    />
                    <text
                      x={CX + (r + 16) * Math.cos(midRad)}
                      y={CY + (r + 16) * Math.sin(midRad) + 4}
                      textAnchor="middle"
                      className="cluster-bluete__peak-label"
                    >
                      {cluster.percentage}
                    </text>
                  </g>
                )}
              </g>
            );
          })}
        </svg>

        {tooltip && (
          <div className="cluster-bluete__tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
            <strong>{tooltip.name}</strong>
            {tooltip.summary}
            <br />
            Bewertung: {tooltip.value} %
          </div>
        )}
      </div>

      <p className="cluster-bluete__hint">
        Tipp: Fahre mit der Maus über ein Blütenblatt (oder tippe darauf), um den genauen Wert zu
        sehen.
      </p>

      <details className="table-toggle">
        <summary>Bewertungsbereiche als Liste anzeigen</summary>
        <table className="cluster-bluete__table">
          <thead>
            <tr>
              <th>Bereich</th>
              <th>Bewertung</th>
            </tr>
          </thead>
          <tbody>
            {clusters.map((cluster) => (
              <tr key={cluster.clusterId}>
                <td>{cluster.title}</td>
                <td>
                  <div className="cluster-bluete__bar-cell">
                    <div className="cluster-bluete__bar-track">
                      <div
                        className="cluster-bluete__bar-fill"
                        style={{ width: `${cluster.percentage}%` }}
                      />
                    </div>
                    <span>{cluster.percentage} %</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}
